import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { getModalPortalTarget } from '../lib/portalTarget';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import { useGuardedClose } from './useGuardedClose';

const WIDTH: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export interface GuardedModalProps {
  open: boolean;
  title: string;
  /** Der Dialog hält ungespeicherte Eingaben */
  dirty: boolean;
  busy?: boolean;
  onClose: () => void;
  /** Für die Rückfrage (Standard: title) */
  label?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Knopfzeile; „Abbrechen" darin ruft requestClose */
  footer?: (requestClose: () => void) => ReactNode;
  testAnchor?: string;
  children: ReactNode;
}

/**
 * Dialog-Rahmen mit Schutz (Entscheid E6): Klick daneben, X und Escape fragen nach,
 * sobald etwas eingegeben ist. Für Dialoge zu einzelnen Einträgen und kurzen Aktionen.
 */
export function GuardedModal({ open, title, dirty, busy = false, onClose, label, size = 'md', footer, testAnchor, children }: GuardedModalProps) {
  const { requestClose, confirmProps } = useGuardedClose({ open, dirty, busy, onClose, label: label ?? title });
  if (!open) return null;

  // Die Rückfrage liegt NEBEN dem Hintergrund, nicht darin: Klicks steigen in React auch
  // durch Portale zum Eltern-Element auf und würden die Rückfrage sofort wieder öffnen.
  return createPortal(
    <>
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-4" onClick={requestClose} data-test-anchor={testAnchor}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-slate-900 border border-slate-800 rounded-xl w-full ${WIDTH[size]} max-h-[92dvh] flex flex-col shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 flex-shrink-0">
          <h3 className="font-semibold text-slate-100 min-w-0 truncate">{title}</h3>
          <button
            type="button"
            onClick={requestClose}
            disabled={busy}
            aria-label="Schließen"
            className="text-slate-400 hover:text-slate-200 min-h-[44px] min-w-[44px] flex items-center justify-center disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4">{children}</div>
        {footer && (
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 border-t border-slate-800 flex-shrink-0">
            {footer(requestClose)}
          </div>
        )}
      </div>
    </div>
    <UnsavedChangesModal {...confirmProps} />
    </>,
    getModalPortalTarget(),
  );
}
